import { ErrorModal } from "@dexteel/mesf-core";
import { Box, Paper, Typography } from "@mui/material";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	isUsableDate,
	urlParam,
	useUrlState,
} from "@/shared/hooks/useUrlState";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { useTimerSpeedReport } from "../hooks/useTimerSpeedReport";
import { isPlottable } from "../models/TimerSpeedReport";
import { TimerSpeedReportSummary } from "./TimerSpeedReportSummary";
import { TimerSpeedReportToolbar } from "./TimerSpeedReportToolbar";
import { TimerSpeedScatter } from "./TimerSpeedScatter";

// The legacy opens on the last three days: start = today - 2, end = today. Both
// the initial state and the Reset button read the range from here, so they cannot
// drift apart.
const defaultStart = () => moment().startOf("day").subtract(2, "days");
const defaultEnd = () => moment().startOf("day");

const URL_SCHEMA = {
	start: urlParam.date(defaultStart),
	end: urlParam.date(defaultEnd),
};

export const TimerSpeedReport = () => {
	const [{ start, end }, setFilters, { resetValues }] = useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const dataQuery = useTimerSpeedReport({
		start: startStr,
		end: endStr,
	});

	const points = useMemo(() => dataQuery.data ?? [], [dataQuery.data]);

	const counts = useMemo(() => {
		const products = new Set<string>();
		let plotted = 0;
		for (const point of points) {
			if (!isPlottable(point)) continue;
			plotted++;
			products.add(point.ProductName);
		}
		return { plotted, products: products.size };
	}, [points]);

	// Back to the range the screen opens on. When the dates are already the
	// default they do not change, so nothing would refetch — the explicit refetch
	// is what makes the button also pull billets rolled since the page loaded.
	const handleReset = useCallback(() => {
		resetValues();
		dataQuery.refetch();
	}, [dataQuery, resetValues]);

	// Assigned from an effect so dismissing the modal sticks: setting it during
	// render re-raises the same error as soon as onHide clears it.
	useEffect(() => {
		const error = dataQuery.error as Error | null;
		if (error) setErrorMsg(error.message);
	}, [dataQuery.error]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Timer Speed Report
			</Typography>

			<Paper elevation={3}>
				<TimerSpeedReportToolbar
					start={start}
					end={end}
					onStartChange={(d) => isUsableDate(d) && setFilters({ start: d })}
					onEndChange={(d) => isUsableDate(d) && setFilters({ end: d })}
					onReset={handleReset}
					isLoading={dataQuery.isFetching}
				/>
			</Paper>

			<TimerSpeedReportSummary
				plotted={counts.plotted}
				total={points.length}
				products={counts.products}
			/>

			<Paper elevation={3} sx={{ flex: 1, overflow: "hidden", minHeight: 300 }}>
				{dataQuery.isLoading ? (
					<CenteredLazyLoading />
				) : (
					<TimerSpeedScatter points={points} />
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
